import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
    X, Plus, Edit2, Trash2, Search, Folder, ChevronRight, ChevronDown,
} from 'react-feather';
import Button from '../../Buttons/Button';
import './PortfolioGroupsModal.scss';

let _uid = Date.now();
const uid = () => 'g' + (++_uid).toString(36);

const PortfolioGroupsModal = ({ portfolios, products, groups: initialGroups, onSave, onClose }) => {
    const [groups, setGroups] = useState(() =>
        initialGroups.map(g => ({ ...g, expanded: false }))
    );
    const [query, setQuery] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [openMenuFor, setOpenMenuFor] = useState(null);
    const [menuPos, setMenuPos] = useState(null);
    const [recently, setRecently] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [confirmOverwrite, setConfirmOverwrite] = useState(null); // [{ name, addedFundNames }] entries for Linked groups receiving members
    const [selected, setSelected] = useState(new Set());
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);
    const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
    const [bulkMenuPos, setBulkMenuPos] = useState(null);
    const [activeTag, setActiveTag] = useState(null);
    const bulkBtnRef = useRef(null);

    // Derived state
    const availableTags = (products || []).filter(t => !t.disabled);
    const portfolioMap = new Map(portfolios.map(p => [p.fundNumber, p.fundName]));
    const grouped = new Set(groups.flatMap(g => g.fundNumbers));
    const ungrouped = portfolios.filter(p => !grouped.has(p.fundNumber));
    const filteredUngrouped = ungrouped
        .filter(p => !activeTag || p.offerName === activeTag)
        .filter(p => !query.trim() || p.fundName.toLowerCase().includes(query.toLowerCase()));
    const totalAssigned = groups.reduce((n, g) => n + g.fundNumbers.length, 0);

    // Escape closes modal (but rename input handles its own Escape)
    useEffect(() => {
        function onKey(e) { if (e.key === 'Escape') onClose(); }
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    // Flash animation
    function flash(fundNumber) {
        setRecently({ fundNumber, ts: Date.now() });
        setTimeout(
            () => setRecently(r => r?.fundNumber === fundNumber ? null : r),
            1600
        );
    }

    function moveToGroup(fundNumber, gid) {
        setGroups(gs => gs.map(g => ({
            ...g,
            fundNumbers: g.id === gid
                ? Array.from(new Set([...g.fundNumbers, fundNumber]))
                : g.fundNumbers.filter(fn => fn !== fundNumber),
        })));
        flash(fundNumber);
    }

    function moveToUngrouped(fundNumber) {
        setGroups(gs => gs.map(g => ({
            ...g,
            fundNumbers: g.fundNumbers.filter(fn => fn !== fundNumber),
        })));
        flash(fundNumber);
    }

    function bulkAddToGroup(gid) {
        setGroups(gs => gs.map(g => ({
            ...g,
            fundNumbers: g.id === gid
                ? Array.from(new Set([...g.fundNumbers, ...selected]))
                : g.fundNumbers.filter(fn => !selected.has(fn)),
        })));
        selected.forEach(fn => flash(fn));
        setSelected(new Set());
        setBulkMenuOpen(false);
    }

    function bulkAddGroupFromSelection() {
        const id = uid();
        setGroups(gs => {
            const cleaned = gs.map(g => ({
                ...g,
                fundNumbers: g.fundNumbers.filter(fn => !selected.has(fn)),
            }));
            return [...cleaned, { id, name: 'Untitled group', fundNumbers: Array.from(selected), expanded: true, linked: true }];
        });
        selected.forEach(fn => flash(fn));
        setSelected(new Set());
        setBulkMenuOpen(false);
        setEditingId(id);
    }

    function toggleSelected(fundNumber) {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(fundNumber) ? next.delete(fundNumber) : next.add(fundNumber);
            return next;
        });
    }

    function toggleSelectAll() {
        if (selected.size === filteredUngrouped.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(filteredUngrouped.map(p => p.fundNumber)));
        }
    }

    function addGroup() {
        const id = uid();
        setGroups(gs => [...gs, { id, name: 'Untitled group', fundNumbers: [], expanded: true, linked: true }]);
        setEditingId(id);
    }

    function addGroupFromPortfolio(fundNumber) {
        const id = uid();
        setGroups(gs => {
            const cleaned = gs.map(g => ({
                ...g,
                fundNumbers: g.fundNumbers.filter(fn => fn !== fundNumber),
            }));
            return [...cleaned, { id, name: 'Untitled group', fundNumbers: [fundNumber], expanded: true, linked: true }];
        });
        setOpenMenuFor(null);
        setEditingId(id);
        flash(fundNumber);
    }

    function renameGroup(id, name) {
        setGroups(gs =>
            gs.map(g => g.id === id ? { ...g, name: name || 'Untitled group' } : g)
        );
    }

    function deleteGroup(id) {
        const g = groups.find(g => g.id === id);
        if (g && g.fundNumbers.length > 0) {
            setConfirmDelete({ id, name: g.name, count: g.fundNumbers.length });
        } else {
            setGroups(gs => gs.filter(g => g.id !== id));
        }
    }

    function confirmDeleteGroup() {
        setGroups(gs => gs.filter(g => g.id !== confirmDelete.id));
        setConfirmDelete(null);
    }

    function toggleExpanded(id) {
        setGroups(gs => gs.map(g => g.id === id ? { ...g, expanded: !g.expanded } : g));
    }

    // Compute which Linked groups would have a member's content overwritten by
    // this save. Returns [] when nothing is destructive.
    function computeDestructiveAdds() {
        const initialById = new Map(initialGroups.map(g => [g.id, g]));
        const out = [];
        for (const g of groups) {
            if (g.linked === false) continue;  // Individual groups don't sync content
            const original = initialById.get(g.id);
            const originalSet = new Set(original ? original.fundNumbers : []);
            const added = g.fundNumbers.filter(fn => !originalSet.has(fn));
            if (added.length === 0) continue;
            // If this is a brand-new group (no original), the "primary" is the
            // first added fund and it doesn't overwrite anything itself; only
            // the rest do.
            const destructiveAdds = original ? added : added.slice(1);
            if (destructiveAdds.length === 0) continue;
            out.push({
                groupId: g.id,
                groupName: g.name,
                addedFundNames: destructiveAdds.map(fn => portfolioMap.get(fn) || fn),
            });
        }
        return out;
    }

    async function emitSave() {
        // Ref guard: onSave POSTs group creates against a snapshot that only
        // refreshes after the response, so a second click while in flight
        // would create the group twice. Button `disabled` is CSS-only.
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        try {
            // Preserve `linked` on the wire. The modal never intentionally changes
            // it (Linked/Individual toggle lives in the selector's kebab popup),
            // so stripping it would let diffGroups misread `undefined` as
            // "Linked" and silently flip every Individual group on save.
            await onSave(groups.map(({ id, name, fundNumbers, linked }) => ({ id, name, fundNumbers, linked })));
        } finally {
            // On success the parent unmounts the modal; on failure it stays
            // open, so re-enable the button for retry.
            savingRef.current = false;
            setSaving(false);
        }
    }

    function handleSave() {
        if (savingRef.current) return;
        const destructive = computeDestructiveAdds();
        if (destructive.length === 0) {
            emitSave();
            return;
        }
        setConfirmOverwrite(destructive);
    }

    // ── PortfolioRow (nested to close over state) ──────────────────────────────
    function PortfolioRow({ fundNumber, inGroup }) {
        const name = portfolioMap.get(fundNumber) || fundNumber;
        const isFlashing = recently?.fundNumber === fundNumber;
        const isChecked = selected.has(fundNumber);

        let rowClass = 'pgm2-row';
        if (isFlashing) rowClass += ' pgm2-row--flash';
        if (isChecked) rowClass += ' pgm2-row--checked';

        const menuOpen = openMenuFor === fundNumber;

        return (
            <div className={rowClass} onClick={!inGroup ? () => toggleSelected(fundNumber) : undefined}>
                {!inGroup && (
                    <input
                        type="checkbox"
                        className="pgm2-checkbox"
                        checked={isChecked}
                        onChange={() => toggleSelected(fundNumber)}
                        onClick={e => e.stopPropagation()}
                    />
                )}
                <span className="pgm2-row-name" title={name}>{name}</span>
                {inGroup ? (
                    <button
                        className="pgm2-icon-btn pgm2-icon-btn--danger-hover"
                        title="Remove from group"
                        onClick={e => { e.stopPropagation(); moveToUngrouped(fundNumber); }}
                    >
                        <X size={13} />
                    </button>
                ) : (
                    <div className="pgm2-add-wrap" onClick={e => e.stopPropagation()}>
                        <button
                            className={`pgm2-icon-btn pgm2-icon-btn--accent${menuOpen ? ' pgm2-icon-btn--open' : ''}`}
                            title={selected.size > 1 && selected.has(fundNumber) ? `Add ${selected.size} selected to group` : 'Add to group'}
                            onClick={e => {
                                const isBulk = selected.size > 1 && selected.has(fundNumber);
                                if (isBulk) {
                                    const r = e.currentTarget.getBoundingClientRect();
                                    setBulkMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
                                    setBulkMenuOpen(true);
                                    setOpenMenuFor(null);
                                } else {
                                    if (openMenuFor !== fundNumber) {
                                        const r = e.currentTarget.getBoundingClientRect();
                                        setMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
                                    }
                                    setOpenMenuFor(prev => prev === fundNumber ? null : fundNumber);
                                }
                            }}
                        >
                            <Plus size={14} />
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // ── GroupCard ──────────────────────────────────────────────────────────────
    function GroupCard({ group }) {
        const isEditing = editingId === group.id;
        const inputRef = useRef(null);
        const [localName, setLocalName] = useState(group.name);

        useEffect(() => {
            if (isEditing && inputRef.current) {
                inputRef.current.focus();
                inputRef.current.select();
            }
        }, [isEditing]);

        useEffect(() => {
            setLocalName(group.name);
        }, [group.name]);

        function commitRename() {
            renameGroup(group.id, localName);
            setEditingId(null);
        }

        function cancelRename() {
            setLocalName(group.name);
            setEditingId(null);
        }

        return (
            <div className="pgm2-group-card">
                {/* Group header */}
                <div className="pgm2-group-head">
                    <button
                        className="pgm2-icon-btn pgm2-chevron-btn"
                        style={{ transform: group.expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                        onClick={() => toggleExpanded(group.id)}
                        title={group.expanded ? 'Collapse' : 'Expand'}
                    >
                        <ChevronRight size={14} />
                    </button>

                    <Folder size={15} className="pgm2-group-folder-icon" />

                    {isEditing ? (
                        <input
                            ref={inputRef}
                            className="pgm2-rename-input"
                            value={localName}
                            onChange={e => setLocalName(e.target.value)}
                            onBlur={commitRename}
                            onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); inputRef.current?.blur(); }
                                if (e.key === 'Escape') { e.stopPropagation(); cancelRename(); }
                            }}
                        />
                    ) : (
                        <button
                            className="pgm2-group-name-btn"
                            onClick={() => { setEditingId(group.id); setLocalName(group.name); }}
                            title="Click to rename"
                        >
                            {group.name}
                        </button>
                    )}

                    <span className="pgm2-count-pill">{group.fundNumbers.length}</span>

                    <button
                        className="pgm2-icon-btn"
                        title="Rename group"
                        onClick={() => { setEditingId(group.id); setLocalName(group.name); }}
                    >
                        <Edit2 size={13} />
                    </button>

                    <button
                        className="pgm2-icon-btn pgm2-icon-btn--danger-hover"
                        title="Delete group"
                        onClick={() => deleteGroup(group.id)}
                    >
                        <Trash2 size={13} />
                    </button>
                </div>

                {/* Group body (when expanded) */}
                {group.expanded && (
                    <div className="pgm2-group-body">
                        {group.fundNumbers.length === 0 ? (
                            <div className="pgm2-drop-zone">
                                Use the + button on the right to add portfolios
                            </div>
                        ) : (
                            <ul className="pgm2-row-list">
                                {group.fundNumbers.map(fn => (
                                    <li key={fn}>
                                        <PortfolioRow fundNumber={fn} inGroup groupId={group.id} />
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // ── Render ─────────────────────────────────────────────────────────────────
    const modal = (
        <div className="pgm2-backdrop" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="pgm2-container">

                {/* Header */}
                <div className="pgm2-header">
                    <div className="pgm2-header-text">
                        <div className="pgm2-title">Portfolio Groups</div>
                        <div className="pgm2-subtitle">
                            Select portfolios on the right and use "Add to group" to assign them.
                        </div>
                    </div>
                    <button className="pgm2-icon-btn pgm2-close-btn" onClick={onClose} title="Close">
                        <X size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="pgm2-body">

                    {/* Left column — groups */}
                    <div className="pgm2-col-groups">
                        <div className="pgm2-col-header">
                            <span className="pgm2-col-label">Groups</span>
                            <button className="pgm2-new-group-btn" onClick={addGroup}>
                                <Plus size={13} />
                                New group
                            </button>
                        </div>

                        <div className="pgm2-col-scroll">
                            {groups.length === 0 ? (
                                <div className="pgm2-groups-empty">
                                    No groups yet.<br />Click "New group" to get started.
                                </div>
                            ) : (
                                groups.map(group => (
                                    <GroupCard key={group.id} group={group} />
                                ))
                            )}
                        </div>
                    </div>

                    {/* Right column — ungrouped portfolios */}
                    <div className="pgm2-col-ungrouped">
                        {availableTags.length > 0 && (
                            <div className="pgm2-tag-filter">
                                <button
                                    className={`pgm2-tag-chip${!activeTag ? ' pgm2-tag-chip--active' : ''}`}
                                    onClick={() => setActiveTag(null)}
                                >
                                    All
                                </button>
                                {availableTags.map(tag => (
                                    <button
                                        key={tag.offerName}
                                        className={`pgm2-tag-chip${activeTag === tag.offerName ? ' pgm2-tag-chip--active' : ''}`}
                                        onClick={() => setActiveTag(prev => prev === tag.offerName ? null : tag.offerName)}
                                    >
                                        {tag.offerName}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="pgm2-col-header">
                            <div className="pgm2-search-box">
                                <Search size={13} className="pgm2-search-icon" />
                                <input
                                    className="pgm2-search-input"
                                    placeholder="Search portfolios…"
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                />
                                {query && (
                                    <button
                                        className="pgm2-icon-btn"
                                        onClick={() => setQuery('')}
                                        title="Clear search"
                                    >
                                        <X size={13} />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="pgm2-col-scroll">
                            {filteredUngrouped.length === 0 ? (
                                <div className="pgm2-ungrouped-empty">
                                    {query.trim()
                                        ? 'No portfolios match your search.'
                                        : 'All portfolios are grouped.'}
                                </div>
                            ) : (
                                <>
                                    {/* Select all + bulk actions row */}
                                    <div className="pgm2-select-all-row">
                                        <input
                                            type="checkbox"
                                            className="pgm2-checkbox"
                                            checked={filteredUngrouped.length > 0 && selected.size === filteredUngrouped.length}
                                            onChange={toggleSelectAll}
                                            onClick={e => e.stopPropagation()}
                                            ref={el => {
                                                if (el) el.indeterminate = selected.size > 0 && selected.size < filteredUngrouped.length;
                                            }}
                                        />
                                        <span className="pgm2-select-all-label" onClick={toggleSelectAll}>Select all</span>
                                        {selected.size > 0 && (
                                            <div className="pgm2-bulk-actions">
                                                <button
                                                    ref={bulkBtnRef}
                                                    className={`pgm2-bulk-add-btn${bulkMenuOpen ? ' pgm2-bulk-add-btn--open' : ''}`}
                                                    onClick={() => {
                                                        if (!bulkMenuOpen && bulkBtnRef.current) {
                                                            const r = bulkBtnRef.current.getBoundingClientRect();
                                                            setBulkMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
                                                        }
                                                        setBulkMenuOpen(v => !v);
                                                    }}
                                                >
                                                    Add to group
                                                    <ChevronDown size={13} />
                                                </button>
                                                <button className="pgm2-icon-btn" title="Clear selection" onClick={() => setSelected(new Set())}>
                                                    <X size={13} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <ul className="pgm2-row-list">
                                        {filteredUngrouped.map(p => (
                                            <li key={p.fundNumber}>
                                                <PortfolioRow fundNumber={p.fundNumber} inGroup={false} />
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="pgm2-footer">
                    <div className="pgm2-stats">
                        <span><strong>{totalAssigned}</strong> grouped</span>
                        <span className="pgm2-bullet">•</span>
                        <span><strong>{ungrouped.length}</strong> ungrouped</span>
                        <span className="pgm2-bullet">•</span>
                        <span><strong>{portfolios.length}</strong> total</span>
                    </div>
                    <div className="pgm2-footer-actions">
                        <Button variant="secondary" onClick={onClose}>Cancel</Button>
                        <Button primary disabled={saving} loading={saving} onClick={handleSave}>Save groups</Button>
                    </div>
                </div>

                {/* Confirm delete overlay */}
                {confirmDelete && (
                    <div className="pgm2-confirm-overlay">
                        <div className="pgm2-confirm-dialog">
                            <div className="pgm2-confirm-title">Delete group?</div>
                            <div className="pgm2-confirm-body">
                                <strong>{confirmDelete.name}</strong> contains {confirmDelete.count}{' '}
                                {confirmDelete.count === 1 ? 'portfolio' : 'portfolios'}.
                                Deleting the group will not delete the portfolios — they will become ungrouped.
                            </div>
                            <div className="pgm2-confirm-actions">
                                <Button small variant="secondary" onClick={() => setConfirmDelete(null)}>
                                    Cancel
                                </Button>
                                <Button small variant="danger" onClick={confirmDeleteGroup}>
                                    Delete
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Confirm Linked-group content overwrite on save */}
                {confirmOverwrite && (
                    <div className="pgm2-confirm-overlay">
                        <div className="pgm2-confirm-dialog">
                            <div className="pgm2-confirm-title">Some changes will overwrite content</div>
                            <div className="pgm2-confirm-body">
                                <p style={{ marginTop: 0 }}>The following portfolios will have their content replaced with the group's content:</p>
                                <ul style={{ paddingLeft: '1.2rem', margin: 0, listStyle: 'none' }}>
                                    {confirmOverwrite.map(entry => (
                                        <li key={entry.groupId} style={{ marginBottom: 10 }}>
                                            <div>
                                                <strong>{entry.groupName}</strong> <em>(Linked)</em> — adding{' '}
                                                {entry.addedFundNames.length === 1
                                                    ? '1 portfolio:'
                                                    : `${entry.addedFundNames.length} portfolios:`}
                                            </div>
                                            <ol style={{ paddingLeft: '1.4rem', margin: '4px 0 0' }}>
                                                {entry.addedFundNames.map((name, i) => (
                                                    <li key={i} style={{ marginBottom: 2 }}>{name}</li>
                                                ))}
                                            </ol>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="pgm2-confirm-actions">
                                <Button small variant="secondary" onClick={() => setConfirmOverwrite(null)}>
                                    Cancel
                                </Button>
                                <Button small variant="danger" disabled={saving} loading={saving} onClick={() => { setConfirmOverwrite(null); emitSave(); }}>
                                    Save and overwrite
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const addToGroupMenu = openMenuFor && menuPos && ReactDOM.createPortal(
        <>
            <div className="pgm2-bulk-menu-backdrop" onClick={() => setOpenMenuFor(null)} />
            <div
                className="pgm2-add-menu"
                style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, left: 'auto', zIndex: 2000 }}
            >
                <div className="pgm2-add-menu-caption">ADD TO GROUP</div>
                {groups.length === 0 ? (
                    <div className="pgm2-add-menu-empty">No groups yet</div>
                ) : (
                    groups.map(g => (
                        <button key={g.id} className="pgm2-add-menu-row" onClick={() => { moveToGroup(openMenuFor, g.id); setOpenMenuFor(null); }}>
                            <Folder size={13} className="pgm2-add-menu-folder" />
                            <span className="pgm2-add-menu-name">{g.name}</span>
                            <span className="pgm2-add-menu-count">{g.fundNumbers.length}</span>
                        </button>
                    ))
                )}
                <div className="pgm2-add-menu-divider" />
                <button className="pgm2-add-menu-new" onClick={() => { addGroupFromPortfolio(openMenuFor); setOpenMenuFor(null); }}>
                    <Plus size={13} />
                    New group from this…
                </button>
            </div>
        </>,
        document.body
    );

    const bulkMenu = bulkMenuOpen && bulkMenuPos && ReactDOM.createPortal(
        <>
            <div className="pgm2-bulk-menu-backdrop" onClick={() => setBulkMenuOpen(false)} />
            <div
                className="pgm2-add-menu"
                style={{ position: 'fixed', top: bulkMenuPos.top, right: bulkMenuPos.right, left: 'auto', zIndex: 2000 }}
            >
                <div className="pgm2-add-menu-caption">ADD TO GROUP</div>
                {groups.length === 0 ? (
                    <div className="pgm2-add-menu-empty">No groups yet</div>
                ) : (
                    groups.map(g => (
                        <button key={g.id} className="pgm2-add-menu-row" onClick={() => bulkAddToGroup(g.id)}>
                            <Folder size={13} className="pgm2-add-menu-folder" />
                            <span className="pgm2-add-menu-name">{g.name}</span>
                            <span className="pgm2-add-menu-count">{g.fundNumbers.length}</span>
                        </button>
                    ))
                )}
                <div className="pgm2-add-menu-divider" />
                <button className="pgm2-add-menu-new" onClick={bulkAddGroupFromSelection}>
                    <Plus size={13} />
                    New group from selection…
                </button>
            </div>
        </>,
        document.body
    );

    const portal = document.querySelector('#modal-portal');
    return (
        <>
            {portal ? ReactDOM.createPortal(modal, portal) : modal}
            {bulkMenu}
            {addToGroupMenu}
        </>
    );
};

export default PortfolioGroupsModal;
